/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  combineReducers,
  composeReducers,
  composeSetters,
  entityValidationSetter,
  formStateSnapshot,
  getFormStateSnapshotValue,
  getInvalidFields,
  markFormStateAsTouched,
  removeDirtyRow,
  resetDirtyType,
  setCreatedDate,
  setDirty,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
  setNewDirtyType,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  Table,
} from '@gh/common-shared';
import { FormMode } from '@gh/core-ui';
import { arrayIterator, differentFieldsWith } from '@gh/core-util';
import { first, pick } from 'lodash';
import {
  complementQuoteVariation,
  getQuoteVariationId,
  HlpQuoteDetailsInitResponse,
  QuoteVariation,
  quoteVariationTable,
  validateQuoteVariation,
} from '../../../resources/hlp-quote.model';
import {
  queryQuoteInvalidVariations,
  queryQuoteNotPriceBookVariations,
  queryQuoteTopFormHasErrors,
  queryQuoteVariationsHasErrors,
  HlpQuoteDetailsState,
  QuoteDetailsStateEvent,
  QuoteDetailsStateEventType,
} from './hlp-quote-details.state';

const primaryDataReducer = composeReducers(
  quoteMasterInfoReducer,
  combineReducers({
    variations: quoteVariationsReducer,
  }),
  quoteBottomReducer,
);

function dataReducer(state: HlpQuoteDetailsState, event: QuoteDetailsStateEvent): HlpQuoteDetailsState {
  const newState = primaryDataReducer(state, event);
  if (state === newState) {
    if (event.type === QuoteDetailsStateEventType.VariationSetField) {
      return globalFlagsReducer(state, newState, event);
    }
    return state;
  }
  return globalFlagsReducer(state, newState, event);
}

const setVariationField = composeSetters(
  setEntityField,
  setUpdatedDirtyType,
  entityValidationSetter(validateQuoteVariation));

export function quoteDetailsReducer(state: HlpQuoteDetailsState, event: QuoteDetailsStateEvent): HlpQuoteDetailsState {
  switch (event.type) {
    case QuoteDetailsStateEventType.Load:
      return loadDetails(
        state,
        event.init,
        event.supportsProfit,
        event.isProfitVisible,
        event.mode || state.formMode);
    case QuoteDetailsStateEventType.MergeOnSave:
      return mergeOnSave(state, event.saveResponse);
    case QuoteDetailsStateEventType.MergeOnRecalculate:
      return mergeOnRecalculate(state, event.saveResponse);
    case QuoteDetailsStateEventType.Approved:
    case QuoteDetailsStateEventType.Unapproved:
      return mergeOnApprove(state, event.details);
    case QuoteDetailsStateEventType.UpdateVersion:
      return setEntityField(state, 'version', event.version);
    case QuoteDetailsStateEventType.SetMasterInfoShadow:
    case QuoteDetailsStateEventType.CommitMasterInfo:
    case QuoteDetailsStateEventType.VariationAddSet:
    case QuoteDetailsStateEventType.VariationSetField:
    case QuoteDetailsStateEventType.VariationRemove:
    case QuoteDetailsStateEventType.VariationMoveCreditDebit:
    case QuoteDetailsStateEventType.SetBottomField:
      return dataReducer(state, event);
    case QuoteDetailsStateEventType.SetProfitVisibility:
      return profitVisibilityReducer(state, event);
    case QuoteDetailsStateEventType.NavigateToInvalid:
    case QuoteDetailsStateEventType.NavigateToNotPriceBookVariation:
    case QuoteDetailsStateEventType.ResetFocusedCell:
    case QuoteDetailsStateEventType.SetVariationSelection:
      return selectionReducer(state, event);
    case QuoteDetailsStateEventType.ChangeVariationListFilter:
      return changeVariationsFilterReducer(state, event);
    default:
      return state;
  }
}

function quoteMasterInfoReducer(state: HlpQuoteDetailsState, event: QuoteDetailsStateEvent): HlpQuoteDetailsState {
  switch (event.type) {
    case QuoteDetailsStateEventType.SetMasterInfoShadow:
      return setEntityFields(state, {
        'masterInfoShadow': event.snapshot,
        'derivedInfo': event.derivedInfo || state.derivedInfo,
      });
    case QuoteDetailsStateEventType.CommitMasterInfo:
      return setEntityFields(state, {
        'masterInfo': state.masterInfoShadow,
        'variations': event.resetVariations ? quoteVariationTable.create() : state.variations,
      });
    default:
      return state;
  }
}

function quoteBottomReducer(state: HlpQuoteDetailsState, event: QuoteDetailsStateEvent): HlpQuoteDetailsState {
  switch (event.type) {
    case QuoteDetailsStateEventType.SetBottomField:
      return setNestedEntityField(state, ['bottom', event.name], event.value);
    default:
      return state;
  }
}

function profitVisibilityReducer(state: HlpQuoteDetailsState, event: QuoteDetailsStateEvent): HlpQuoteDetailsState {
  switch (event.type) {
    case QuoteDetailsStateEventType.SetProfitVisibility:
      return setNestedEntityField(state, ['settings', 'isProfitVisible'], event.visible);
    default:
      return state;
  }
}

function quoteVariationsReducer(variations: Table<QuoteVariation>,
                                 event: QuoteDetailsStateEvent): Table<QuoteVariation> {
  switch (event.type) {
    case QuoteDetailsStateEventType.VariationAddSet:
      return quoteVariationTable.insertAll(
        variations,
        event.variations
          .map(complementQuoteVariation)
          .map(setCreatedDate)
          .map(setNewDirtyType));
    case QuoteDetailsStateEventType.VariationSetField:
      return quoteVariationTable.mergeById(
        variations,
        event.variationId,
        (variation) => setVariationField(variation, event.name, event.value));
    case QuoteDetailsStateEventType.VariationRemove:
      return quoteVariationTable.mergeById(variations, event.variationId, removeDirtyRow);
    case QuoteDetailsStateEventType.VariationMoveCreditDebit:
      return quoteVariationTable.mergeById(
        variations,
        event.variationId,
        (variation) => setVariationField(variation, 'isCredit', !variation.isCredit));
    default:
      return variations;
  }
}

function globalFlagsReducer(oldState: HlpQuoteDetailsState,
                            newState: HlpQuoteDetailsState,
                            event: QuoteDetailsStateEvent): HlpQuoteDetailsState {
  switch (event.type) {
    case QuoteDetailsStateEventType.CommitMasterInfo: {
      const differentFields = differentFieldsWith(
        getFormStateSnapshotValue(oldState.masterInfo),
        getFormStateSnapshotValue(newState.masterInfo));
      if (differentFields.includes('salesTaxRates')) {
        return setDirty(setRecalculationNeeded(newState));
      } else {
        return setDirty(newState);
      }
    }
    case QuoteDetailsStateEventType.VariationAddSet:
    case QuoteDetailsStateEventType.VariationRemove:
    case QuoteDetailsStateEventType.VariationMoveCreditDebit:
      return setDirty(setRecalculationNeeded(newState));
    case QuoteDetailsStateEventType.VariationSetField:
      return event.name === 'quantity' ? setDirty(setRecalculationNeeded(newState)) : setDirty(newState);
    case QuoteDetailsStateEventType.SetBottomField:
      return setDirty(newState);
    default:
      return newState;
  }
}

function selectionReducer(state: HlpQuoteDetailsState, event: QuoteDetailsStateEvent): HlpQuoteDetailsState {
  switch (event.type) {
    case QuoteDetailsStateEventType.NavigateToInvalid:
      return navigateToInvalid(state);
    case QuoteDetailsStateEventType.NavigateToNotPriceBookVariation:
      return navigateToNotPriceBookVariation(state);
    case QuoteDetailsStateEventType.ResetFocusedCell:
      return setNestedEntityField(state, ['selection', 'focusedCell'], void 0);
    case QuoteDetailsStateEventType.SetVariationSelection:
      return variationListSelectionReducer(state, event);
    default:
      return state;
  }
}

/**
 * Reducer to save the state of selected checkboxes under gh-hlp-quote-details-variation-list component.
 * Only saves the list of selected checkboxes (SelectionModel).
 */
function variationListSelectionReducer(state: HlpQuoteDetailsState, event: QuoteDetailsStateEvent): HlpQuoteDetailsState {
  if (event.type === QuoteDetailsStateEventType.SetVariationSelection) {
    return setNestedEntityField(state, ['selection', 'variationSelection'], event.selectionModel);
  }
  return state;
}

function changeVariationsFilterReducer(state: HlpQuoteDetailsState, event: QuoteDetailsStateEvent): HlpQuoteDetailsState {
  switch (event.type) {
    case QuoteDetailsStateEventType.ChangeVariationListFilter:
      return setEntityField(state, 'variationsFilter', event.variationsFilter);
    default:
      return state;
  }
}

function navigateToNotPriceBookVariation(state: HlpQuoteDetailsState): HlpQuoteDetailsState {
  const notPriceBookVariation = first(queryQuoteNotPriceBookVariations(state));
  if (notPriceBookVariation) {
    return setNestedEntityField(
      state,
      ['selection', 'focusedCell'],
      { id: getQuoteVariationId(notPriceBookVariation), field: 'itemCode' });
  } else {
    return state;
  }
}

function navigateToInvalid(state: HlpQuoteDetailsState): HlpQuoteDetailsState {
  if (queryQuoteTopFormHasErrors(state)) {
    return setEntityField(state, 'masterInfoShadow', markFormStateAsTouched(state.masterInfoShadow));
  } else if (queryQuoteVariationsHasErrors(state)) {
    const invalidVariation = first(queryQuoteInvalidVariations(state));
    const invalidField = invalidVariation ? first(getInvalidFields(invalidVariation)) : void 0;
    if (invalidVariation && invalidField) {
      return setNestedEntityField(
        state,
        ['selection', 'focusedCell'],
        { id: getQuoteVariationId(invalidVariation), field: invalidField });
    }
  }

  return state;
}

function loadDetails(state: HlpQuoteDetailsState,
                     init: HlpQuoteDetailsInitResponse,
                     supportsProfit: boolean,
                     isProfitVisible: boolean,
                     mode: FormMode): HlpQuoteDetailsState {
  const masterInfo = formStateSnapshot(init.masterInfo);

  return {
    ...state,
    masterInfo,
    masterInfoShadow: masterInfo,
    formMode: mode,
    derivedInfo: init.derivedInfo,
    variations: quoteVariationTable.create(arrayIterator(init.variations)),
    settings: {
      isSalesEstimateShown: init.isSalesEstimateShown,
      isQuickQuotesAllowed: init.isQuickQuotesAllowed,
      useSalesEstimate: init.useSalesEstimate,
      isGtoTopExist: init.isGtoTopExist,
      supportsProfit,
      isProfitVisible: supportsProfit && isProfitVisible,
    },
    version: init.version,
  };
}

function mergeOnRecalculate(state: HlpQuoteDetailsState, saveResponse: HlpQuoteDetailsInitResponse): HlpQuoteDetailsState {
  return {
    ...state,
    isRecalculationNeeded: false,
    derivedInfo: setEntityFields(state.derivedInfo, saveResponse.derivedInfo),
    variations: quoteVariationTable.mergeAll(
      state.variations,
      saveResponse.variations,
      (oldVariation, newVariation) => setEntityFields(oldVariation, pick(newVariation, ['profit', 'price', 'memo', 'quantity', 'grayOut', 'priceWithGst']))),
  };
}

function mergeOnApprove(state: HlpQuoteDetailsState, details: HlpQuoteDetailsInitResponse): HlpQuoteDetailsState {
  return {
    ...state,
    version: details.version,
    derivedInfo: setEntityFields(state.derivedInfo, {
      approvedDate: details.derivedInfo.approvedDate,
      approvedByName: details.derivedInfo.approvedByName,
    }),
  };
}

function mergeOnSave(state: HlpQuoteDetailsState, saveResponse: HlpQuoteDetailsInitResponse): HlpQuoteDetailsState {
  return {
    ...state,
    version: saveResponse.version,
    isDirty: false,
    isRecalculationNeeded: false,
    derivedInfo: setEntityFields(state.derivedInfo, saveResponse.derivedInfo),
    variations: quoteVariationTable.mergeAll(
      state.variations,
      saveResponse.variations,
      (oldVariation, newVariation) => resetDirtyType(setEntityFields(
        oldVariation!,
        pick(newVariation, ['profit', 'price', 'memo', 'quantity', 'grayOut', 'priceWithGst'])))),
  };
}
